// MultiTimeFrameSignalTable.jsx  (PART 1/3)

import React, { useState, useEffect, useMemo } from 'react';
import './MTFLatestTradingSignalsFromCache.css';
import Select, { components } from 'react-select';
import PositionDetails from './PositionDetails';
import ManualSignalOrderFormBySymbol from './ManualSignalOrderFormBySymbol';
import { buildScopedApiUrl, ensureArray, getApiHeaders } from "./config";
import { useWorkspace } from './workspace/WorkspaceContext';
/**
 * All backend fields from MultiTimeFrameTradingSignal
 * (ordered & grouped for better UX)
 */
const columns = [
  'timeFrame',
  'symbol',
  'priceXEma12Trend',
  'priceXEma21Trend',
  'priceXEma36Trend',
  'priceXEma50Trend',
  'priceXEma100Trend',
  'priceXEma200Trend',
  'priceXVWAP24Trend',
  'priceXVWAPWeeklyTrend',
  'appEma12XEma21Trend',
  'ema12XEma36Trend',
  'appEma21XEma36Trend',
  'appEma50XEma200Trend',
  'appEma100XEma200Trend',
  'appEma50XEma100Trend',
  'appPriceXEma12Cross',
  'appPriceXEma21Cross',
  'appPriceXEma36Cross',
  'appPriceXEma50Cross',
  'appPriceXEma100Cross',
  'appPriceXEma200Cross',
  'appPriceXVwap24Cross',
  'appPriceXVwapWeeklyCross',
  'appEma50XEma200Cross',
  'mtfPriceXEma100Ema200Cross',
  'mtfPriceXEma100Ema200TrendOnlyCross',  
  'ema12XEma36Cross', //may need to remove
  'appPriceXEma12Ema21Cross',
  'appEma12XEma21Cross',
  'mtfPriceXEma21Ema36Cross',
  'appEma21XEma36Cross',
  'appEma21XEma36CrossEntrySRCrossSignal',
  'mtfPriceXEma21Ema36TrendOnlyCross',
  'appMtfEmaConfirmedCross',
  'priceXSRTrendLineCross',
  'priceXChannelLineCross',
  'priceXSupplyDemandZoneCross',
  'priceXSrConfirmedCross',
  'appPriceXSrConfirmedCross',
  'appPriceXZoneConfirmedCross',
  'appPriceXPosCloseSrConfirmedCross',
  'stochRsiCross',
  'rsiReverse',
  'stochRsiLevel',
  'rsiLevel',
  'sRsiAndRsiComboReversal',
  'trendBasedStochRsiEntrySignal',
  'trendBasedExtremeStochRsiEntrySignal',
  'ignoreEmaSignalRangeSrCross',
  'szDzZoneBasedStochRsiEntrySignal',
  'appRsiDivEntrySignal',
  'appAoDivEntrySignal',
  'appTouchCloseSrCrossSignal',
  'swingHighCloseSrCrossSignal',
  'swingLowCloseSrCrossSignal',
  'swingHighWickSrCrossSignal',
  'swingLowWickSrCrossSignal',
  'ema12ema21SwingHighCloseSrCrossSignal',
  'ema12ema21SwingLowCloseSrCrossSignal',
  'ema12ema21SwingHighWickSrCrossSignal',
  'ema12ema21SwingLowWickSrCrossSignal',
  'ema12ema21SwingLevelStochRsiEntrySignal',
  'swingLowTakenOut',
  'swingHighTakenOut',
  'swingLowWickTakenOut',
  'swingHighWickTakenOut',
  'swingLevelStochRsiEntrySignal',
  'dualFlexiEmaEntryExit',
  'ictManualTradeEntryExitSrCross',
  'fvgTagged',
  'priceAboveOrBelowAllEmaLevels',
  'allEmaLevelsInOneTrend',
  'singleGodCandleCrossedAllEmaLevels',
  'singleCandleCrossedLowerEmaStack',
  'priceXAllEmaStackEdgeCross',
  'priceXLowerEmaStackEdgeCross',
  'lowerEmaStackEdgeCrossSignalStopLoss',
  'lowerEmaStackEdgeCrossSignalStopLossDistance',
  'priceBeyondAllEmaStackAndEma200Cross',
  'ema200HighPrice',
  'ema200LowPrice',
  'priceInsideEma200Band',
  'ema200BandCross',
  'ema200BandCrossTrendAligned',
  'firstEma50BandCrossAfterTrendShift',
  'firstEma50BandCrossInLast50Candles',
  'firstEma50BandCrossInLast100Candles',
  'firstEma50BandCrossInLast200Candles',
  'firstEma200BandCrossAfterTrendShift',
  'firstEma200BandCrossInLast50Candles',
  'firstEma200BandCrossInLast100Candles',
  'firstEma200BandCrossInLast200Candles',
  'firstTrendAlignedStochRsiResetAfterTrendShift',
  'firstTrendAlignedStochRsiResetAtEma50BandAfterTrendShift',
  'firstTrendAlignedStochRsiResetAtEma200BandAfterTrendShift',
  'ictMSB',
  'ictSwingHighLowRetest',
  'ictSwingHighLowWickRetest',
  'ictSupplyDemandReversal',
  'ictBreakerReversal',
  'candleTrendReversal',
  'candleColor',
  'candlePattern',
  'isWhiteListed',
  'whiteListedTimeFrame',
  'rsiDiv',
  'aoDiv',
  'aoTrend',
  'ccr',
  'adx',
  'volume',
  'volumeChange',
  'volumeBreakout',
  'close',
  'marketPrice',
  'change',
  'candleCloseTimeInSGT',
  'candleCloseTimeInIST',
  'calculatedCandleCloseTime',
];



const headers = [
  'TF',
  'Symbol',
  'Price X EMA12 Trend',
  'Price X EMA21 Trend',
  'Price X EMA36 Trend',
  'Price X EMA50 Trend',
  'Price X EMA100 Trend',
  'Price X EMA200 Trend',
  'Price X VWAP24 Trend',
  'Price X VWAP Weekly Trend',
  'appEMA12 X EMA21 Trend',
  'EMA12 X EMA36 Trend',
  'appEMA21 X EMA36 Trend',
  'appEMA50 X EMA200 Trend',
  'appEMA100 X EMA200 Trend',
  'appEMA50 X EMA100 Trend',
  'appPrice X EMA12 Cross',
  'appPrice X EMA21 Cross',
  'appPrice X EMA36 Cross',  
  'appPrice X EMA50 Cross',
  'appPrice X EMA100 Cross',
  'appPrice X EMA200 Cross',
  'appPrice X VWAP24 Cross',
  'appPrice X VWAP Weekly Cross',
  'appEMA50 X EMA200 Cross',
  'MTF Price X EMA100/200 Cross', 
  'MTF Price X EMA100/200 TrendOnly',
  'EMA12 X EMA36 Cross',
  'appPrice X EMA12/21 Cross',
  'appEMA12 X EMA21 Cross',
  'MTF Price X EMA21/36 Cross',
  'appEMA21 X EMA36 Cross',
  'appEMA21 X EMA36 Entry SR Cross',
  'MTF Price X EMA21/36 TrendOnly',
  'app MTF EMA Confirmed Cross',  
  'Price X SR Trendline Cross',
  'Price X Channel Line Cross',
  'Price X Supply/Demand Zone Cross',
  'Price X SR Confirmed Cross',
  'appPrice X SR Confirmed Cross',
  'appPrice X Zone Confirmed Cross',
  'appPrice X Pos Close SR Confirmed',
  'Stoch RSI Cross',
  'RSI Reversal',
  'Stoch RSI Level',
  'RSI Level',
  'StochRSI + RSI Combo Reversal',
  'Trend Based StochRSI Entry',
  'Trend Based Extreme StochRSI Entry',
  'Ignore EMA Range SR Cross',
  'SZ/DZ Zone StochRSI Entry',
  'app RSI Div Entry',
  'app AO Div Entry',
  'app Touch Close SR Cross',
  'Swing High Close SR Cross',
  'Swing Low Close SR Cross',
  'Swing High Wick SR Cross',
  'Swing Low Wick SR Cross',
  'EMA12/21 Swing High Close SR',
  'EMA12/21 Swing Low Close SR',
  'EMA12/21 Swing High Wick SR',
  'EMA12/21 Swing Low Wick SR',
  'EMA12/21 SwingLevel StochRSI Entry',
  'Swing Low Taken Out',
  'Swing High Taken Out',
  'Swing Low Wick Taken Out',
  'Swing High Wick Taken Out',
  'SwingLevel StochRSI Entry',
  'Dual Flexi EMA Entry/Exit',
  'ICT Manual Trade Entry/Exit SR',
  'FVG Tagged / Swept',
  'Price Above/Below All EMA',
  'All EMAs One Trend',
  'Single God Candle All EMA Cross',
  'Single Candle Cross Lower Stack',
  'Price X All EMA Stack Edge',
  'Price X Lower EMA Stack Edge',
  'LES EdgeCross SL',
  'LES EdgeCross SL Dist%',
  'Price Beyond All EMA & EMA200',
  'EMA200 Band High',
  'EMA200 Band Low',
  'Price Inside EMA200 Band',
  'EMA200 Band Cross',
  'EMA200 Band Cross Trend Aligned',
  'First EMA50 Band Cross After Trend Shift',
  'First EMA50 Band Cross In Last 50',
  'First EMA50 Band Cross In Last 100',
  'First EMA50 Band Cross In Last 200',
  'First EMA200 Band Cross After Trend Shift',
  'First EMA200 Band Cross In Last 50',
  'First EMA200 Band Cross In Last 100',
  'First EMA200 Band Cross In Last 200',
  'First Trend Aligned Stoch RSI Reset After Trend Shift',
  'First Stoch RSI Reset At EMA50 Band After Trend Shift',
  'First Stoch RSI Reset At EMA200 Band After Trend Shift',
  'ICT MSB',
  'ICT Swing HL Retest',
  'ICT Swing HL Wick Retest',
  'ICT Supply/Demand Reversal',
  'ICT Breaker Reversal',
  'Candle Trend Reversal',
  'Candle Color',
  'Candle Pattern',
  'White Listed',
  'White Listed TF',
  'RSI Div',
  'AO Div',
  'AO Trend',
  'CCR',
  'ADX',
  'Volume',
  'Volume Change %',
  'Volume Breakout',
  'Entry Close',
  'CMP',
  'Change %',
  'SGT Time',
  'IST Time',
  'Candle Close (UTC)',
];

// numeric-like fields (for operator filters)
const numericColumns = new Set([
  'timeFrame',
  'whiteListedTimeFrame',
  'adx',
  'volume',
  'volumeChange',
  'close',
  'marketPrice',
  'ema200HighPrice',
  'ema200LowPrice',
  'change',
  'rsiLevel',
  'stochRsiLevel',
]);

// quick header lookup
const headerMap = columns.reduce((acc, col, idx) => {
  acc[col] = headers[idx] || col;
  return acc;
}, {});

// small custom clear icon for react-select
const CustomClearIndicator = (props) => (
  <components.ClearIndicator {...props}>
    <span style={{ color: 'black', fontSize: '16px', cursor: 'pointer' }}>✕</span>
  </components.ClearIndicator>
);

function MTFLatestTradingSignalsFromCache({ isPublic = false }) {
  const { workspace } = useWorkspace();
  // ---------------- Modal & manual order states ----------------
  const [manualOrderParam, setManualOrderParam] = useState({
    timeFrame: null,
    symbol: null,
    side: null,
  });

  const [showModal, setShowModal] = useState(false);
  const [selectedSymbol, setSelectedSymbol] = useState(null);
  const [showManualOrderFormModal, setShowManualOrderFormModal] = useState(false);

  // ---------------- Data & filtering/sorting states ----------------
  const [signals, setSignals] = useState([]);
  const [filteredSignals, setFilteredSignals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // multi-select filters (string fields) -> key => [values...]
  const [multiFilters, setMultiFilters] = useState({});
  // numeric filters (with >, <, ranges, etc.) -> key => expression string
  const [numericFilters, setNumericFilters] = useState({});

  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  // column visibility panel
  const [visibleColumns, setVisibleColumns] = useState(() => {
    const saved = localStorage.getItem('visibleColumns_v2');
    if (saved) return JSON.parse(saved);
    return columns.reduce((acc, col) => {
      acc[col] = true;
      return acc;
    }, {});
  });
  const [showColumnPanel, setShowColumnPanel] = useState(false);

  // floating row focus (double-clicked row)
  const [focusedRowIndex, setFocusedRowIndex] = useState(null);

  // --------------- Modal & manual order handlers ---------------
  const openPositionModal = (symbol) => {
    setSelectedSymbol(symbol);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedSymbol(null);
  };

  const openManualOrderFormModal = (symbol, priceXEma12Trend, timeFrame) => {
    if (priceXEma12Trend === 'BULL') {
      setManualOrderParam({ symbol, side: 'open_long', timeFrame });
    } else if (priceXEma12Trend === 'BEAR') {
      setManualOrderParam({ symbol, side: 'open_short', timeFrame });
    } else {
      setManualOrderParam({ symbol, side: null, timeFrame });
    }
    setShowManualOrderFormModal(true);
  };

  const handleCloseManualOrderFormModal = () => {
    setShowManualOrderFormModal(false);
  };

  // --------------- Column visibility handlers ---------------
  const handleToggleColumn = (col) => {
    setVisibleColumns((prev) => {
      const updated = {
        ...prev,
        [col]: !prev[col],
      };
      localStorage.setItem('visibleColumns_v2', JSON.stringify(updated));
      return updated;
    });
  };

  // --------------- Fetch MTFS data from backend ---------------
  useEffect(() => {
    setLoading(true);
    fetch(
      buildScopedApiUrl('/api/bot/ui/mtf/latestSignals', workspace, {
        mode: isPublic ? "PUBLIC" : "PRIVATE",
      }),
      {
        headers: getApiHeaders(),
      })
      .then((res) => res.json())
      .then((data) => {
        const normalizedSignals = ensureArray(data);
        if (!Array.isArray(data)) {
          console.warn('Unexpected latest MTF signals payload shape', workspace, data);
        }
        setSignals(normalizedSignals);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Error fetching signals:', err);
        setError('Failed to load data. Please try again later or check API.');
        setLoading(false);
      });
  }, [isPublic, workspace]);
  // --------------- TradingView open helper ---------------
  const openTradingViewChart = (symbol) => {
    const cleanedSymbol = symbol.replace(/\s*(_UMCBL)$/, '');
    const suffix = workspace.marketType === 'PERPS' ? '.P' : '';
    const tradingViewUrl = `https://www.tradingview.com/chart/?symbol=${workspace.exchange}:${cleanedSymbol}${suffix}`;
    window.open(tradingViewUrl, '_blank');
  };

  // --------------- Distinct values for filters ---------------
  const getDistinctValues = (column) => {
    const all = ensureArray(signals)
      .map((item) => item[column])
      .filter((v) => v !== null && v !== undefined && v !== '');
    const set = new Set(all);
    const arr = Array.from(set);
    // numeric-friendly sort for TF / whitelist TF
    if (column === 'timeFrame' || column === 'whiteListedTimeFrame') {
      return arr.sort((a, b) => Number(a) - Number(b));
    }
    return arr.sort((a, b) => String(a).localeCompare(String(b)));
  };

  // --------------- Numeric filter parser (>, <, 10-20, etc.) ---------------
  const parseNumericFilter = (filterStr, value) => {
    if (typeof value !== 'number' || isNaN(value)) return false;
    const trimmed = (filterStr ?? '').toString().trim();
    if (!trimmed) return true;

    // range 10-20
    if (/^\d+(\.\d+)?-\d+(\.\d+)?$/.test(trimmed)) {
      const [min, max] = trimmed.split('-').map(Number);
      return value >= min && value <= max;
    }
    // >=x
    if (/^>=\d+(\.\d+)?$/.test(trimmed)) {
      const num = parseFloat(trimmed.slice(2));
      return value >= num;
    }
    // <=x
    if (/^<=\d+(\.\d+)?$/.test(trimmed)) {
      const num = parseFloat(trimmed.slice(2));
      return value <= num;
    }
    // >x
    if (/^>\d+(\.\d+)?$/.test(trimmed)) {
      const num = parseFloat(trimmed.slice(1));
      return value > num;
    }
    // <x
    if (/^<\d+(\.\d+)?$/.test(trimmed)) {
      const num = parseFloat(trimmed.slice(1));
      return value < num;
    }
    // =x (plain number)
    if (/^\d+(\.\d+)?$/.test(trimmed)) {
      const num = parseFloat(trimmed);
      return value === num;
    }
    return false;
  };

  // --------------- Filter handlers ---------------
  const handleMultiFilterChange = (field, selectedOptions) => {
    setMultiFilters((prev) => {
      const next = { ...prev };
      const values = (selectedOptions || []).map((opt) => opt.value);
      if (values.length === 0) {
        delete next[field];
      } else {
        next[field] = values;
      }
      return next;
    });
  };

  const handleNumericFilterChange = (field, expr) => {
    setNumericFilters((prev) => {
      const next = { ...prev };
      const v = expr.trim();
      if (!v) {
        delete next[field];
      } else {
        next[field] = v;
      }
      return next;
    });
  };

  const clearAllFilters = () => {
    setMultiFilters({});
    setNumericFilters({});
  };

  // --------------- Sorting ---------------
  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return {
          key,
          direction: prev.direction === 'asc' ? 'desc' : 'asc',
        };
      }
      return { key, direction: 'asc' };
    });
  };

  const parseRsiLike = (val) => {
    if (val == null) return -9999;
    if (typeof val === 'string') {
      const lower = val.toLowerCase();
      if (lower.includes('overbought')) return 1000;
      if (lower.includes('oversold')) return -1000;
    }
    const parsed = parseFloat(val);
    return isNaN(parsed) ? -9999 : parsed;
  };

  // --------------- Core effect: apply filters + sort ---------------
  useEffect(() => {
    if (!signals || signals.length === 0) {
      setFilteredSignals([]);
      return;
    }

    let result = signals.filter((signal) => {
      // numeric filters
      const numericOK = Object.entries(numericFilters).every(([field, expr]) => {
        if (!expr) return true;
        const raw = signal[field];
        const num = raw == null ? NaN : parseFloat(raw);
        return parseNumericFilter(expr, num);
      });
      if (!numericOK) return false;

      // multi-select filters (string equality, multi)
      const multiOK = Object.entries(multiFilters).every(([field, values]) => {
        if (!values || values.length === 0) return true;
        const raw = signal[field] ?? '';
        return values.includes(raw);
      });
      if (!multiOK) return false;

      return true;
    });

    // apply sorting
    const { key, direction } = sortConfig;
    if (key) {
      const dirFactor = direction === 'asc' ? 1 : -1;
      result = [...result].sort((a, b) => {
        let valA = a[key];
        let valB = b[key];

        if (key === 'rsiLevel' || key === 'stochRsiLevel') {
          valA = parseRsiLike(valA);
          valB = parseRsiLike(valB);
        } else if (key === 'change') {
          valA = parseFloat(String(valA).replace('%', ''));
          valB = parseFloat(String(valB).replace('%', ''));
        } else if (numericColumns.has(key)) {
          valA = parseFloat(valA);
          valB = parseFloat(valB);
        } else {
          valA = valA == null ? '' : String(valA).toLowerCase();
          valB = valB == null ? '' : String(valB).toLowerCase();
        }

        if (valA < valB) return -1 * dirFactor;
        if (valA > valB) return 1 * dirFactor;
        return 0;
      });
    }

    setFilteredSignals(result);
  }, [signals, numericFilters, multiFilters, sortConfig]);

  // --------------- Filter toolbar rendering helpers ---------------
  const renderFilterControl = (field) => {
    // numeric filter → text input with operators
    if (numericColumns.has(field)) {
      return (
        <input
          type="text"
          className="numeric-filter-input"
          placeholder=">20, 10-30, 50..."
          value={numericFilters[field] || ''}
          onChange={(e) => handleNumericFilterChange(field, e.target.value)}
        />
      );
    }

    // string / categorical filter → multi-select
    const options = getDistinctValues(field).map((v) => ({
      label: String(v),
      value: String(v),
    }));

    const currentValues = multiFilters[field] || [];
    const currentSelected = currentValues.map((v) => ({
      label: v,
      value: v,
    }));

    return (
      <Select
        isMulti
        className="filter-select"
        classNamePrefix="react-select"
        options={options}
        value={currentSelected}
        onChange={(selected) => handleMultiFilterChange(field, selected)}
        isClearable
        isSearchable
        placeholder={`Filter ${headerMap[field] || field}`}
        components={{ ClearIndicator: CustomClearIndicator }}
        menuPortalTarget={document.body}
        menuPosition="absolute"
        styles={{
          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
          control: (base) => ({ ...base, minHeight: '30px', fontSize: '12px' }),
          valueContainer: (base) => ({ ...base, paddingRight: '30px' }),
        }}
      />
    );
  };

  // --------------- Active filter chips ---------------
  const filterChips = useMemo(() => {
    const chips = [];

    Object.entries(multiFilters).forEach(([field, values]) => {
      values.forEach((val) => {
        chips.push({
          type: 'multi',
          field,
          value: val,
          label: `${headerMap[field] || field}: ${val}`,
        });
      });
    });

    Object.entries(numericFilters).forEach(([field, expr]) => {
      if (!expr) return;
      chips.push({
        type: 'numeric',
        field,
        value: expr,
        label: `${headerMap[field] || field}: ${expr}`,
      });
    });

    return chips;
  }, [multiFilters, numericFilters]);

  const handleRemoveChip = (chip) => {
    if (chip.type === 'multi') {
      setMultiFilters((prev) => {
        const next = { ...prev };
        const cur = next[chip.field] || [];
        const filtered = cur.filter((v) => v !== chip.value);
        if (filtered.length === 0) {
          delete next[chip.field];
        } else {
          next[chip.field] = filtered;
        }
        return next;
      });
    } else {
      setNumericFilters((prev) => {
        const next = { ...prev };
        delete next[chip.field];
        return next;
      });
    }
  };

  // --------------- Floating row indicator (double-click focus) ---------------
  const focusedRow =
    focusedRowIndex != null && focusedRowIndex >= 0 && focusedRowIndex < filteredSignals.length
      ? filteredSignals[focusedRowIndex]
      : null;

// --- Filter Panel State ---
const [isFilterOpen, setIsFilterOpen] = useState(
  () => JSON.parse(localStorage.getItem("mtfFilterOpen") || "true")
);

const toggleFilterPanel = () => {
  const next = !isFilterOpen;
  setIsFilterOpen(next);
  localStorage.setItem("mtfFilterOpen", JSON.stringify(next));
};

// --- Filter Search ---
const [filterSearch, setFilterSearch] = useState("");

const filterMatches = (header) =>
  header.toLowerCase().includes(filterSearch.toLowerCase());



  if (loading) return <div className="loading-message">Loading...</div>;
  return (
    <div className="mtf-signal-container">
      <h2 className="page-title">Multi-Time Frame Trading Signals</h2>

      {error && <div className="error-banner">⚠️ {error}</div>}

      {/* -------------- FILTER TOOLBAR -------------- */}
      <div className="mtf-filter-toolbar">
        <div className="mtf-filter-header">
          <span className="mtf-filter-title">Filters</span>
          <span className="mtf-filter-count">
            Showing <strong>{filteredSignals.length}</strong> of{' '}
            <strong>{signals.length}</strong> rows
          </span>
        </div>

        

        <div className="mtf-filter-grid">
          {columns.map((field) => (
            <div key={field} className="mtf-filter-item">
              <label className="mtf-filter-label">{headerMap[field] || field}</label>
              {renderFilterControl(field)}
            </div>
          ))}
        </div>

        <div className="mtf-filter-actions">
          <button
            className="mtf-filter-clear"
            onClick={clearAllFilters}
            type="button"
          >
            Clear All Filters
          </button>
        </div>

        {filterChips.length > 0 && (
          <div className="mtf-filter-chips">
            {filterChips.map((chip, idx) => (
              <span key={idx} className="mtf-chip">
                {chip.label}
                <button
                  type="button"
                  className="mtf-chip-remove"
                  onClick={() => handleRemoveChip(chip)}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* -------------- COLUMN MANAGEMENT (top-right) -------------- */}
      <div className={`column-toggle-dropdown ${showColumnPanel ? 'open' : ''}`}>
        <button onClick={() => setShowColumnPanel((prev) => !prev)}>
          Show / Hide Columns
        </button>
        <button onClick={clearAllFilters}>Clear All Filters</button>

        {showColumnPanel && (
          <div className="column-toggle-panel">
            {columns.map((col) => {
              const isAlwaysVisible = col === 'timeFrame' || col === 'symbol';
              return isAlwaysVisible ? (
                <label key={col} style={{ opacity: 0.6, fontWeight: 'bold' }}>
                  <input type="checkbox" checked disabled />
                  {headerMap[col]} (Fixed)
                </label>
              ) : (
                <label key={col}>
                  <input
                    type="checkbox"
                    checked={visibleColumns[col]}
                    onChange={() => handleToggleColumn(col)}
                  />
                  {headerMap[col]}
                </label>
              );
            })}

            <div className="column-toggle-actions">
              <button
                onClick={() => {
                  const allVisible = columns.reduce((acc, col) => {
                    acc[col] = true;
                    return acc;
                  }, {});
                  setVisibleColumns(allVisible);
                  localStorage.setItem('visibleColumns_v2', JSON.stringify(allVisible));
                }}
              >
                Select All
              </button>
              <button
                onClick={() => {
                  const allHidden = columns.reduce((acc, col) => {
                    acc[col] =
                      col === 'timeFrame' || col === 'symbol' ? true : false;
                    return acc;
                  }, {});
                  setVisibleColumns(allHidden);
                  localStorage.setItem('visibleColumns_v2', JSON.stringify(allHidden));
                }}
              >
                Hide All
              </button>
              <button
                onClick={() => {
                  localStorage.removeItem('visibleColumns_v2');
                  const defaultVisible = columns.reduce((acc, col) => {
                    acc[col] = true;
                    return acc;
                  }, {});
                  setVisibleColumns(defaultVisible);
                }}
              >
                Reset Default
              </button>
            </div>
          </div>
        )}
      </div>


      

      {/* -------------- TABLE -------------- */}
      <div className="table-container full-height">
        <table className="signal-table">
          <thead>
            <tr>
              {headers.map((label, idx) =>
                visibleColumns[columns[idx]] ? (
                  <th
                    key={columns[idx]}
                    className={`sortable 
                      ${
                        sortConfig.key === columns[idx]
                          ? 'sorted-' + sortConfig.direction
                          : ''
                      }`}
                    onClick={() => handleSort(columns[idx])}
                  >
                    {label}
                    <svg viewBox="0 0 20 20" className="sort-icon">
                      <path
                        d={
                          sortConfig.key === columns[idx]
                            ? sortConfig.direction === 'asc'
                              ? 'M5 12l5-5 5 5H5z' // ▲
                              : 'M5 8l5 5 5-5H5z' // ▼
                            : 'M5 8l5 5 5-5H5z' // default ▼
                        }
                      />
                    </svg>
                  </th>
                ) : null
              )}
            </tr>
          </thead>

          <tbody>
            {filteredSignals.map((row, idx) => {
              const isFocused = focusedRowIndex === idx;
              return (
                <tr
                  key={idx}
                  className={isFocused ? 'focused-row' : ''}
                  onDoubleClick={() => setFocusedRowIndex(idx)}
                >
                  {columns.map((col, colIdx) =>
                    visibleColumns[col] ? (
                      <td
                        key={col}
                        className={`
                          ${
                            typeof row[col] === 'string' &&
                            /bull|green|long|oversold/i.test(row[col])
                              ? 'highlight-green'
                              : ''
                          }
                          ${
                            typeof row[col] === 'string' &&
                            /bear|red|short|overbought/i.test(row[col])
                              ? 'highlight-red'
                              : ''
                          }
                          ${
                            col === 'volumeChange' &&
                            parseFloat(row[col]) > 500
                              ? 'highlight-blue'
                              : ''
                          }
                          ${
                            col === 'adx' &&
                            parseFloat(row[col]) < 20
                              ? 'highlight-pink'
                              : ''
                          }
                          ${
                            col === 'adx' &&
                            parseFloat(row[col]) > 40
                              ? 'highlight-orange'
                              : ''
                          }
                          ${
                            col === 'symbol' &&
                            row.side &&
                            row.side.toLowerCase() === 'open_long'
                              ? 'highlight-green'
                              : ''
                          }
                          ${
                            col === 'symbol' &&
                            row.side &&
                            row.side.toLowerCase() === 'open_short'
                              ? 'highlight-red'
                              : ''
                          }
                          ${
                            col === 'change' &&
                            parseFloat(row[col]) > 1
                              ? 'blink-green'
                              : ''
                          }
                          ${
                            col === 'change' &&
                            parseFloat(row[col]) < -1
                              ? 'blink-red'
                              : ''
                          }
                        `}
                      >
                        {col === 'symbol' ? (
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                            }}
                          >
                            <span>
                              {row[col]?.replace(
                                /\s*(LONG|SHORT)$/i,
                                ''
                              )}
                            </span>

                            <span
                              className="chart-icon"
                              onClick={() => openTradingViewChart(row[col])}
                              title="View Chart in TradingView"
                              style={{ cursor: 'pointer' }}
                            >
                              📈
                            </span>

                          </div>
                        ) : (
                          row[col]
                        )}
                      </td>
                    ) : null
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>

        {/* POSITION DETAILS MODAL */}
        {showModal && selectedSymbol && (
          <div className="modal-overlay">
            <div className="modal-content">
              <button className="close-modal" onClick={closeModal}>
                ❌
              </button>
              <PositionDetails symbol={selectedSymbol} />
            </div>
          </div>
        )}

        {/* MANUAL ORDER FORM MODAL */}
        {showManualOrderFormModal && (
          <ManualSignalOrderFormBySymbol
            isOpen={showManualOrderFormModal}
            closeModal={handleCloseManualOrderFormModal}
            symbol={manualOrderParam.symbol}
            side={manualOrderParam.side}
            timeFrame={manualOrderParam.timeFrame}
          />
        )}
      </div>

      {/* -------------- FLOATING ROW INDICATOR -------------- */}
      {focusedRow && (
        <div className="floating-row-indicator">
          <span className="floating-row-symbol">
            {focusedRow.symbol}
          </span>
          <span className="floating-row-sep">•</span>
          <span className="floating-row-tf">
            {focusedRow.timeFrame}m
          </span>
        </div>
      )}
    </div>
  );
}

export default MTFLatestTradingSignalsFromCache;



